import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, LockReset } from '@mui/icons-material';
import { usersAPI, rolesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const emptyForm = { username: '', email: '', full_name: '', password: '', role_id: '' };

const UsersTab = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch {
      setError('Failed to load users');
    }
  };

  const loadRoles = async () => {
    try {
      const response = await rolesAPI.getAll();
      setRoles(response.data);
    } catch {
      setError('Failed to load roles');
    }
  };

  // A user is protected from deletion/deactivation if they're the last
  // remaining Admin - matches the backend guard exactly, so the button is
  // disabled with an explanation rather than round-tripping to find out.
  const activeAdminCount = users.filter((u) => u.is_system_role && u.is_active).length;
  const isLastAdmin = (u) => u.is_system_role && activeAdminCount <= 1;

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ username: u.username, email: u.email, full_name: u.full_name, password: '', role_id: u.role_id || '' });
    setError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setError('');
  };

  const handleSubmit = async () => {
    if (!editing && (!form.username.trim() || !form.password.trim())) {
      setError('Username and password are required');
      return;
    }
    if (!form.full_name.trim() || !form.role_id) {
      setError('Full name and role are required');
      return;
    }
    try {
      if (editing) {
        await usersAPI.update(editing.user_id, {
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          role_id: form.role_id,
        });
        setMessage('User updated successfully');
      } else {
        await usersAPI.create({
          username: form.username.trim(),
          email: form.email.trim(),
          full_name: form.full_name.trim(),
          password: form.password,
          role_id: form.role_id,
        });
        setMessage('User created successfully');
      }
      handleClose();
      loadUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleToggleActive = async (u) => {
    if (isLastAdmin(u) && u.is_active) {
      setError(`Can't deactivate ${u.username} - they're the last remaining Admin.`);
      return;
    }
    try {
      await usersAPI.update(u.user_id, { is_active: !u.is_active });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async (u) => {
    if (isLastAdmin(u)) {
      setError(`Can't delete ${u.username} - they're the last remaining Admin.`);
      return;
    }
    if (!window.confirm(`Delete user "${u.username}"? They will no longer be able to log in.`)) return;
    try {
      await usersAPI.delete(u.user_id);
      setMessage('User deleted successfully');
      loadUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const DEFAULT_RESET_PASSWORD = '1234';

  const openResetPassword = (u) => {
    setResetTarget(u);
    setResetPassword(DEFAULT_RESET_PASSWORD);
    setError('');
  };

  const handleResetPassword = async () => {
    if (resetPassword.trim().length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }
    try {
      await usersAPI.resetPassword(resetTarget.user_id, resetPassword.trim());
      setMessage(`Password reset for ${resetTarget.username}`);
      setResetTarget(null);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Typography variant="h6">Users</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
          Add User
        </Button>
      </Box>

      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && !open && !resetTarget && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Username</strong></TableCell>
              <TableCell><strong>Full Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Role</strong></TableCell>
              <TableCell><strong>Active</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.user_id} hover>
                  <TableCell>
                    {u.username}
                    {u.user_id === currentUser?.user_id && <Chip label="You" size="small" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{u.full_name}</TableCell>
                  <TableCell>{u.email || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.role_name || '—'}
                      size="small"
                      color={u.is_system_role ? 'secondary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={isLastAdmin(u) && u.is_active ? "Can't deactivate the last remaining Admin" : ''}>
                      <span>
                        <Switch
                          size="small"
                          checked={u.is_active}
                          disabled={isLastAdmin(u) && u.is_active}
                          onChange={() => handleToggleActive(u)}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(u)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openResetPassword(u)} title="Reset Password">
                      <LockReset fontSize="small" />
                    </IconButton>
                    <Tooltip title={isLastAdmin(u) ? "Can't delete the last remaining Admin" : ''}>
                      <span>
                        <IconButton size="small" color="error" disabled={isLastAdmin(u)} onClick={() => handleDelete(u)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <TextField
            fullWidth
            label="Username"
            value={form.username}
            disabled={!!editing}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Full Name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          {!editing && (
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              helperText="At least 6 characters"
              sx={{ mb: 2 }}
            />
          )}
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={form.role_id}
              onChange={(e) => setForm({ ...form, role_id: e.target.value })}
            >
              {roles.map((r) => (
                <MenuItem key={r.role_id} value={r.role_id}>{r.role_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editing ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onClose={() => setResetTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password{resetTarget ? ` — ${resetTarget.username}` : ''}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <TextField
            fullWidth
            autoFocus
            label="New Password"
            type="password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            helperText='Defaults to "1234" - edit to set a custom password instead (min 4 characters). No need to know the old one.'
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained">Reset</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersTab;
